import { defineNuxtPlugin, useRuntimeConfig } from '#imports'

import { isSupported } from '../utils/isSupported'

const ACTIVATION_COOKIE = 'ds-visual-coverage'

function markComponentElements(
  instance: { $el?: unknown; $?: { subTree?: { children?: unknown } } },
  attribute: string,
  name: string,
) {
  if (
    instance.$el &&
    typeof instance.$el === 'object' &&
    'nodeType' in instance.$el &&
    (instance.$el as Node).nodeType === Node.ELEMENT_NODE
  ) {
    ;(instance.$el as Element).setAttribute(attribute, name)
    return
  }

  // Handle multi-root (fragment) components
  const children = instance.$?.subTree?.children
  if (Array.isArray(children)) {
    for (const child of children) {
      if (
        child &&
        typeof child === 'object' &&
        'el' in child &&
        child.el &&
        typeof child.el === 'object' &&
        'nodeType' in child.el &&
        (child.el as Node).nodeType === Node.ELEMENT_NODE
      ) {
        ;(child.el as Element).setAttribute(attribute, name)
      }
    }
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig().public.dsVisualCoverage

  if (!config.enabled) return

  // The mixin only activates when the activation cookie is present (or in local dev).
  // This keeps the module zero-cost for regular users in production — the CI crawler
  // sets the cookie before navigating, so only crawler sessions are affected.
  const isActivated = () => {
    if (import.meta.dev) return true
    if (typeof document === 'undefined') return false

    const match = document.cookie.match(
      new RegExp(`(?:^|; )${ACTIVATION_COOKIE}=([^;]*)`),
    )

    return match?.[1] === '1'
  }

  if (!isActivated()) return

  if (!isSupported()) {
    // eslint-disable-next-line no-console
    console.warn(
      '[DS Visual Coverage] Browser not supported (requires requestIdleCallback + Web Workers)',
    )

    return
  }

  nuxtApp.vueApp.mixin({
    mounted() {
      // eslint-disable-next-line no-underscore-dangle
      const name = this.$options.name || this.$options.__name
      if (name?.startsWith('Rev')) {
        markComponentElements(this, config.attribute, name)
      }
    },
    updated() {
      // eslint-disable-next-line no-underscore-dangle
      const name = this.$options.name || this.$options.__name
      if (name?.startsWith('Rev')) {
        markComponentElements(this, config.attribute, name)
      }
    },
  })

  const route = () => window.location.pathname

  window.dsVisualCoverage = {
    collect: async () => {
      const { collectCoverageData } =
        await import('../utils/collectCoverageData')
      const { createGetComponentData } =
        await import('../utils/getComponentData')

      const getComponentData = createGetComponentData({
        attribute: config.attribute,
        weights: config.weights,
      })

      return collectCoverageData({
        getComponentData,
        log: config.log,
        route: route(),
      })
    },
    visualize: async () => {
      const { createCalculateDsVisualCoverages, visualizeCoverage } =
        await import('@preply/ds-visual-coverage-web')
      const { createGetComponentData } =
        await import('../utils/getComponentData')

      const getComponentData = createGetComponentData({
        attribute: config.attribute,
        weights: config.weights,
      })

      const calc = createCalculateDsVisualCoverages({
        getComponentData,
        log: config.log,
      })

      calc.run({
        onComplete: (result) => {
          visualizeCoverage({
            result,
            svgRendererAttributeName: 'dsVisualCoverage',
          })
        },
        // eslint-disable-next-line no-console
        onError: console.error,
      })
    },
  }

  // eslint-disable-next-line no-console
  console.info(
    '[DS Visual Coverage] Activated. Use window.dsVisualCoverage.collect() to measure.',
  )
})
